import {
  ActionCreatorWithoutPayload,
  ActionCreatorWithPayload,
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import {
  agentGroupsData,
  agentsData,
} from '../mock-data/agent-conversations-mock-data';
import { Agent } from '../../../ccf-directory/+state/ccf-directory.slice';

export const CCF_CONVERSATIONS_FEATURE_KEY = 'conversations';

export interface Message {
  user: string;
  status: string;
  text: string;
  timestamp: number;
}

export interface Conversations {
  loading: boolean;
  loadMore: boolean;
  page: number;
  user?: Agent;
  messages: Message[];
  scrollToLast: boolean;
  endOfConversation: boolean;
  agents: any;
  agentGroups: any;
  selectedAgent: any;
  selectedAgentType: string;
}

const intialState: Conversations = {
  loading: false,
  loadMore: false,
  page: 0,
  messages: [],
  scrollToLast: false,
  endOfConversation: false,
  agents: agentsData,
  agentGroups: agentGroupsData,
  selectedAgent: agentsData[0],
  selectedAgentType: 'favourite',
};

export const loadLatestConversation = createAsyncThunk(
  'conversations/fetchLatestConversation',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async (args, { getState }: { getState: () => any }) => {
    const messages = [
      {
        user: 'G',
        status: 'available',
        text: 'This is a conversation about lunch and it isn’t recorded at all and I’m very happy about that, because we were talking about Thai food and I’m not really down with Thai food. So I was suggesting that we should grab burgers',
        timestamp: 1635145280718,
      },
      {
        user: 'AS',
        status: 'available',
        text: 'This is now a text that is about my interaction with Liam. Does this need to be tracked somehow?',
        timestamp: 1635145280718,
      },
      {
        user: 'G',
        status: 'available',
        text: 'Yeah, I can help you out with Liam. I’m open now, so go ahead and transfer him over',
        timestamp: 1635145280718,
      },
    ];

    const response = new Promise<Message[]>((resolve, reject) =>
      setTimeout(() => resolve(messages), 1000)
    );
    await response;
    return response;
  }
);

export const fetchMoreConversation = createAsyncThunk(
  'conversations/fetchMoreConversation',
  async (
    args,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    { getState, rejectWithValue }: { getState: () => any; rejectWithValue: any }
  ) => {
    const { page } = getState().conversations;
    if (page < 2) {
      const extraMessages = [
        {
          user: 'G',
          status: 'available',
          text: 'This is a conversation about lunch and it isn’t recorded at all and I’m very happy about that, because we were talking about Thai food and I’m not really down with Thai food. So I was suggesting that we should grab burgers',
          timestamp: 1635145280718,
        },
        {
          user: 'AS',
          status: 'available',
          text: 'This is now a text that is about my interaction with Liam. Does this need to be tracked somehow?',
          timestamp: 1635145280718,
        },
        {
          user: 'G',
          status: 'available',
          text: 'Yeah, I can help you out with Liam. I’m open now, so go ahead and transfer him over',
          timestamp: 1635145280718,
        },
      ];

      const response = new Promise<Message[]>((resolve, reject) =>
        setTimeout(() => resolve(extraMessages), 1000)
      );
      await response;
      return response;
    } else {
      return rejectWithValue({ error: 'End of Pages' });
    }
  }
);

export const ccfConversationsSlice = createSlice({
  name: CCF_CONVERSATIONS_FEATURE_KEY,
  initialState: intialState,
  reducers: {
    updateSelectedConversation: (state, action) => {
      return {
        ...state,
        user: action.payload.user,
        messages: [],
        page: 0,
        endOfConversation: false,
      };
    },
    updatePageCount: (state) => {
      return {
        ...state,
        page: state.page + 1,
      };
    },
    updateSelectedAgent: (state, action) => {
      return {
        ...state,
        selectedAgent: action.payload.agent,
        selectedAgentType: action.payload.type,
      };
    },
    updateAgentGroups: (state, action) => {
      return {
        ...state,
        agentGroups:
          action.payload.type === 'update'
            ? state.agentGroups.map((group: any) => {
                if (group.name === action.payload.agent.name) {
                  return {
                    ...group,
                    ...action.payload.agent,
                  };
                } else {
                  return group;
                }
              })
            : action.payload.type === 'filter'
            ? action.payload.filteredAgentGroups
            : agentGroupsData,
      };
    },
    updateAgents: (state, action) => {
      return {
        ...state,
        agents:
          action.payload.type === 'update'
            ? state.agents.map((agent: any) => {
                if (agent.name === action.payload.agent.name) {
                  return {
                    ...agent,
                    ...action.payload.agent,
                  };
                } else {
                  return agent;
                }
              })
            : action.payload.type === 'filter'
            ? action.payload.filteredAgents
            : agentsData,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadLatestConversation.pending, (state, action) => {
        return {
          ...state,
          loading: true,
          scrollToLast: false,
        };
      })
      .addCase(loadLatestConversation.fulfilled, (state, action) => {
        return {
          ...state,
          messages: [...state.messages, ...action.payload],
          loading: false,
          page: state.page + 1,
          scrollToLast: true,
        };
      });
    builder
      .addCase(fetchMoreConversation.pending, (state, action) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(fetchMoreConversation.fulfilled, (state, action) => {
        return {
          ...state,
          loading: false,
          messages: [...state.messages, ...action.payload],
          page: state.page + 1,
        };
      })
      .addCase(fetchMoreConversation.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          endOfConversation: true,
        };
      });
  },
});

export const ccfConversationsReducer = ccfConversationsSlice.reducer;
export const ccfConversationsActions = ccfConversationsSlice.actions;

/**
 * Function to get Conversation data
 * @param rootState - Conversations
 * @example
 * @returns It returns Conversations
 */
const getConversationsState = (rootState: { conversations: Conversations }) => {
  return rootState[CCF_CONVERSATIONS_FEATURE_KEY];
};

export const getSelectedAgent = createSelector(
  getConversationsState,
  (state) => state.selectedAgent
);

export const getSelectedAgentType = createSelector(
  getConversationsState,
  (state) => state.selectedAgentType
);

export const getLoaderState = createSelector(
  getConversationsState,
  (state) => state.loading
);

export const getSelectedAgentConverstation = createSelector(
  getConversationsState,
  (state) => state.messages
);

export const getLoadMoreState = createSelector(
  getConversationsState,
  (state) => state.loading
);

export const getScrollToEndState = createSelector(
  getConversationsState,
  (state) => state.scrollToLast
);

export const getConversationExhaustBoolean = createSelector(
  getConversationsState,
  (state) => state.endOfConversation
);

export const getAgents = createSelector(
  getConversationsState,
  (state) => state.agents
);

export const getAgentGroups = createSelector(
  getConversationsState,
  (state) => state.agentGroups
);
